import { extractEntityTargets } from '../../lib/entitySnapshotBuilders';

import { isRecord, stringField } from '../core/record';
import type { EntitySnapshotMaps } from './entityBundle';

/** Enriches legacy renderer-row reactions with current entity snapshots. */
export function enrichRendererReactions(
  reactions: unknown,
  snapshots: EntitySnapshotMaps | null | undefined,
  emptyFallback: unknown,
): unknown {
  if (Array.isArray(reactions)) {
    return reactions.map((reaction) => enrichRendererReaction(reaction, snapshots));
  }

  if (!isRecord(reactions)) return reactions ?? emptyFallback;

  return Object.fromEntries(Object.entries(reactions).map(([key, reaction]) => [key, enrichRendererReaction(reaction, snapshots)]));
}

/** Extracts Richdown entity snapshots for the legacy Message row shape. */
export function richdownEntitySnapshots(
  html: unknown,
  snapshots: EntitySnapshotMaps | null | undefined,
): Record<string, Record<string, unknown | null>> {
  if (typeof html !== 'string' || !html.includes('<entity') || !snapshots) return {};

  const targets = extractEntityTargets(html);
  const result: Record<string, Record<string, unknown | null>> = {};
  const channels = snapshotObject(targets.channel, snapshots.channel);
  const profiles = snapshotObject(targets.profile, snapshots.profile);
  const emoji = snapshotObject(targets.emoji, snapshots.emoji);
  if (Object.keys(channels).length > 0) result.channel = channels;
  if (Object.keys(profiles).length > 0) result.profile = profiles;
  if (Object.keys(emoji).length > 0) result.emoji = emoji;
  return result;
}

export function profileSnapshot(snapshots: EntitySnapshotMaps | null | undefined, id: string): unknown | null {
  return snapshots?.profile?.get(id) ?? null;
}

function enrichRendererReaction(reaction: unknown, snapshots: EntitySnapshotMaps | null | undefined): unknown {
  if (!isRecord(reaction)) return reaction;

  const emojiId = stringField(reaction, 'emoji_id');
  const profileId = stringField(reaction, 'profile_id');
  return {
    ...reaction,
    emoji: emojiId ? (emojiSnapshot(snapshots, emojiId) ?? reaction.emoji ?? null) : (reaction.emoji ?? null),
    profile: profileId ? (profileSnapshot(snapshots, profileId) ?? reaction.profile ?? null) : (reaction.profile ?? null),
  };
}

function snapshotObject(
  ids: readonly string[],
  snapshots: ReadonlyMap<string, unknown | null> | undefined,
): Record<string, unknown | null> {
  if (!snapshots) return {};
  return Object.fromEntries(ids.map((id) => [id, snapshots.get(id) ?? null]));
}

function emojiSnapshot(snapshots: EntitySnapshotMaps | null | undefined, id: string): unknown | null {
  return snapshots?.emoji?.get(id) ?? null;
}
